import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import http, { httpSymfony } from 'Utils/http';
import { cachedGet } from 'Utils/cachedGet';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { process } from 'Utils/errorHandler';
import { downloadIfAttachment, readAxiosData } from 'Utils/utils';

export const useFlowRun = (flowId, postParams) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isRunning, setIsRunning] = useState(false);
    const [background, setBackground] = useState(null);
    const [response, setResponse] = useState('');
    const [responseStatus, setResponseStatus] = useState(null);
    const [dialogTitle, setDialogTitle] = useState('Flow Response');
    const [serverTimeouts, setServerTimeouts] = useState(null);
    const [effectiveTimeout, setEffectiveTimeout] = useState(null);

    // Server-side timeout limits (REQUEST_TIMEOUT etc.) — read once, reused to compute the display window.
    useEffect(() => {
        cachedGet('/config/timeouts', httpSymfony)
            .then((res) => setServerTimeouts(res.data))
            .catch(() => {});
    }, []);

    const handleRunFlow = async () => {
        setIsRunning(true);
        const storedToken = (localStorage.getItem('token') || '').replace(/['"]+/g, '');
        let params = { flow_id: flowId, token: storedToken };

        // Merge post parameters body if present
        if (postParams?.body) {
            try {
                const jsonBodyData = JSON.parse(postParams.body);
                params = { ...params, ...jsonBodyData };
            } catch (e) {
                // Invalid JSON, ignore
            }
        }

        // Effective sync-wait window, computed exactly as the backend does:
        // min(param timeout, REQUEST_TIMEOUT). `params.timeout` is the value we actually send (body),
        // REQUEST_TIMEOUT comes from the server limits endpoint.
        const requestTimeout = serverTimeouts?.request_timeout ?? null;
        if (requestTimeout != null) {
            const paramTimeout = params.timeout;
            const parsed = paramTimeout === undefined || paramTimeout === null || paramTimeout === ''
                ? null
                : Number(paramTimeout);
            setEffectiveTimeout(parsed != null && !Number.isNaN(parsed)
                ? Math.min(parsed, requestTimeout)
                : requestTimeout);
        } else {
            setEffectiveTimeout(null);
        }

        // Build URL with query params if present
        let url = `/flow/${flowId}/call`;
        if (postParams?.query) {
            url += `?${postParams.query}`;
        }

        http.post(url, params, { responseType: 'blob' })
            .then(async (res) => {
                if (downloadIfAttachment(res)) {
                    enqueueSnackbar('The file has been downloaded', {
                        action: CustomSnackbar,
                        variant: 'success',
                    });
                    return;
                }
                const data = await readAxiosData(res.data);
                // The flow exceeded the synchronous wait and switched to background waiting:
                // show the response as usual, but keep a "Get result" action to refresh it later.
                if (data && Number(data.status) === 202 && data.requestId) {
                    setBackground({ requestId: data.requestId });
                    setDialogTitle('Running in the background');
                    setResponseStatus(202);
                    setResponse(JSON.stringify(data));
                    return;
                }
                enqueueSnackbar('The flow has been finished', {
                    action: CustomSnackbar,
                    variant: 'success',
                });
                setDialogTitle('Flow Response');
                setResponseStatus(Number(res.headers?.['x-flow-status']) || res.status);
                setResponse(JSON.stringify(data));
            })
        .catch(async (err) => {
            setDialogTitle('Flow Response');
            if (err.response) {
                // `responseType: 'blob'` leaves the body a Blob, but errorHandler reads
                // `data.message` — parse it first or it falls back to a generic message.
                err.response.data = await readAxiosData(err.response.data);
                setResponseStatus(err.response.status);
                setResponse(JSON.stringify(err.response.data));
            } else {
                setResponseStatus(0);
                setResponse(JSON.stringify({ error: err.message || 'Network error' }));
            }
            process(err, enqueueSnackbar);
        })
            .finally(() => {
                setIsRunning(false);
            });
    };

    // Fetch the result of a flow that moved to background waiting.
    // The worker stores it in Symfony/Redis; we read it straight from Symfony
    // (httpSymfony already attaches the Bearer token), so no Laravel proxy is needed.
    const fetchBackgroundResult = useCallback(async () => {
        if (!background) {
            return;
        }
        try {
            const res = await httpSymfony.get(`/get/result/${flowId}-${background.requestId}`, { responseType: 'blob' });
            if (downloadIfAttachment(res)) {
                setBackground(null);
                enqueueSnackbar('The file has been downloaded', {
                    action: CustomSnackbar,
                    variant: 'success',
                });
                return;
            }
            const data = await readAxiosData(res.data);
            const stillBackground = res.status === 202 || (data && Number(data.status) === 202);
            // Always refresh the shown JSON with whatever came back.
            setDialogTitle(stillBackground ? 'Running in the background' : 'Flow Response');
            setResponseStatus(res.status);
            setResponse(JSON.stringify(data));
            // Still running — keep the "Get result" action so the user can retry.
            if (stillBackground) {
                enqueueSnackbar('The flow is still running. Please try again in a moment.', {
                    action: CustomSnackbar,
                    variant: 'info',
                });
                return;
            }
            setBackground(null);
            enqueueSnackbar('The flow has been finished', {
                action: CustomSnackbar,
                variant: 'success',
            });
        } catch (err) {
            setDialogTitle('Flow Response');
            if (err.response) {
                // `responseType: 'blob'` leaves the body a Blob, but errorHandler reads
                // `data.message` — parse it first or it falls back to a generic message.
                err.response.data = await readAxiosData(err.response.data);
                setResponseStatus(err.response.status);
                setResponse(JSON.stringify(err.response.data));
            } else {
                setResponseStatus(0);
                setResponse(JSON.stringify({ error: err.message || 'Network error' }));
            }
            setBackground(null);
            process(err, enqueueSnackbar);
        }
    }, [background, flowId, enqueueSnackbar]);

    const stopBackgroundFlow = () => {
        if (!background) {
            return;
        }
        httpSymfony
            .post(`/flow/${background.requestId}/cancel`)
            .then(() => {
                enqueueSnackbar('Flow stopped', { action: CustomSnackbar, variant: 'success' });
                setBackground(null);
            })
            .catch((err) => {
                enqueueSnackbar(`${err.response?.data?.message || 'Unable to stop flow'}`, {
                    action: CustomSnackbar,
                    variant: 'error',
                });
            });
    };

    const stopSyncFlow = () => {
        http.get(`/user/flow/${flowId}/running`)
            .then((res) => {
                const guid = res.data?.guid;
                if (!guid) {
                    enqueueSnackbar('No running execution to stop yet', { action: CustomSnackbar, variant: 'info' });
                    return null;
                }
                return httpSymfony.post(`/flow/${guid}/cancel`).then(() => {
                    enqueueSnackbar('Flow stopped', { action: CustomSnackbar, variant: 'success' });
                });
            })
            .catch((err) => {
                enqueueSnackbar(`${err.response?.data?.message || 'Unable to stop flow'}`, {
                    action: CustomSnackbar,
                    variant: 'error',
                });
            });
    };

    return {
        runFlow: handleRunFlow,
        isRunning,
        setIsRunning,
        background,
        setBackground,
        response,
        setResponse,
        responseStatus,
        setResponseStatus,
        dialogTitle,
        setDialogTitle,
        effectiveTimeout,
        fetchBackgroundResult,
        stopBackgroundFlow,
        stopSyncFlow,
    };
};
